

































import { computed, defineComponent, onMounted, onUnmounted, ref, set, toRef, watch } from "@vue/composition-api";
import { Route } from "vue-router";
import { CallOptions } from "@scrile/api-provider/dist/api/CallOptionsProvider";
import { PaymentPackage, PaymentPackageEnum } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import { ProfileLink } from "@scrile/api-provider/dist/api/ProfileLinksProvider";
import { SubscriptionLevel } from "@scrile/api-provider/dist/api/SubscriptionLevelProvider";
import { Subscription } from "@scrile/api-provider/dist/api/SubscriptionProvider";
import { User } from "@scrile/api-provider/dist/api/UserProvider";
import {
  ContentFeed,
  ContentFeedSearchResult,
  ContentFeedTypeEnum,
  InputContentFeedSearch,
} from "@scrile/api-provider/dist/projects/scrileConnect/ContentFeedProvider";
import { UserPublicData } from "@scrile/api-provider/dist/projects/scrileConnect/UserPublicDataProvider";
import { checkAuthorities, checkRole } from "@scrile/tools/dist/lib/SecurityHelpers";
import useAppointments from "@/hooks/useAppointments";
import useAuthUser from "@/hooks/useAuthUser";
import useBanners from "@/hooks/useBanners";
import useContentFeed from "@/hooks/useContentFeed";
import useNSFWUserSettings from "@/hooks/useNSFWUserSettings";
import useProfileLinks from "@/hooks/useProfileLinks";
import useRole from "@/hooks/useRole";
import providers from "@/lib/api";
import { getPurchasedContentFeeds } from "@/lib/contentFeedHelper";
import emitter from "@/lib/emitter";
import { userPublicDataFieldsWithStoryInfo } from "@/lib/fieldsHelper";
import { getProfileUrl } from "@/lib/urlHelpers";
import { resolveUserIdByRoute } from "@/lib/userHelpers";
import { feedFields } from "@/helpers";
import config from "@/config";
import { ViewStoriesSettings } from "@/types";
import { EVENTS, SUBSCRIPTION_LEVEL_FRAGMENT } from "@/const";
import useCheckAgeVerification from "@/components/AgeVerification/hooks/useCheckAgeVerification";
import { useCheckCreatorServices } from "@/features/CreatorService/hooks/useCheckCreatorServices";
import PageProfileView from "./components/PageProfileView";

export default defineComponent({
  name: "PageProfile",
  components: { PageProfileView },
  props: {
    userId: { type: String, required: true },
    feedId: { type: String },
    storyId: { type: String },
  },
  setup(props, { root }) {
    const { authorities, authUser, allowAccess } = useAuthUser();
    const { contentFeedSearch } = useContentFeed();
    const { isBookingAvailable, checkBookingAvailable } = useAppointments();
    const { showNotSafeForWork } = useNSFWUserSettings();
    const { banners, getBanners } = useBanners();
    const { checkNeedShowConfirm } = useCheckAgeVerification();
    const { profileLinks, getProfileLinks } = useProfileLinks(computed(() => props.userId));
    const { hasServices, loading: loadingCheckServices, checkServices } = useCheckCreatorServices(
      computed(() => props.userId)
    );

    const loading = ref(true);
    const user = ref<UserPublicData | null>(null);
    const questionaryInfo = ref<Record<string, any>>({});
    const contentFeedSearchResult = ref<ContentFeedSearchResult | null>(null);
    const subscriptionLevels = ref<SubscriptionLevel[]>([]);
    const activeSubscriptions = ref<Subscription[]>([]);
    const paymentPackages = ref<PaymentPackage[]>([]);
    const scroll = ref(false);
    const isFollowing = ref<0 | 1 | -1>(0);
    const userCallOptions = ref<CallOptions | null>(null);
    const { isCreator: isUserCreator } = useRole(user);

    const isSensitiveContent = computed(() => !user.value?.nsfwSettings?.safeForWork);
    const contentFeedSearchSort = computed(() =>
      config.featurePinPost ? "PIN_TIME;PUBLISH_TIME,DESC" : "PUBLISH_TIME,DESC"
    );

    const isAuthor = computed(() => {
      return !!authUser.value && authUser.value.id === props.userId;
    });

    const isCallable = computed(
      () =>
        config.featureCalling &&
        !!user.value &&
        isUserCreator.value &&
        !isAuthor.value &&
        !!userCallOptions.value?.enabled
    );
    const canUpdateContentSet = computed(() => {
      return checkAuthorities(authorities.value, isAuthor.value ? ["createMyContentSet"] : ["createAnyContentSet"]);
    });
    const checkAppointments = async (userId: string) => {
      if (isAuthor.value || !authUser.value) {
        isBookingAvailable.value = false;
        return;
      }
      await checkBookingAvailable(userId);
    };
    const onFeedClick = (feedId: string) => {
      if (user.value) root.$router.push(getProfileUrl(user.value, { feedId }));
    };
    const onFollowUpdate = (status: boolean) => {
      isFollowing.value = status ? 1 : 0;
    };
    const onContentFeedUpdate = (contentFeed: ContentFeed) => {
      if (!contentFeedSearchResult.value) return;

      const result = contentFeedSearchResult.value.result.map(item =>
        item.id === contentFeed.id ? contentFeed : item
      );

      const itemsWithPinTime = result.filter(item => item.pinTime !== null);
      const itemsWithoutPinTime = result.filter(item => item.pinTime === null);

      if (itemsWithPinTime.length && itemsWithPinTime.length > config.maxAllowedPins) {
        const oldestItemWithPinTime = itemsWithPinTime.shift();
        oldestItemWithPinTime && itemsWithoutPinTime.push({ ...oldestItemWithPinTime, pinTime: null });
      }

      const sortedItemsWithPinTime = [...itemsWithPinTime].sort(
        (a, b) => new Date(a.pinTime ?? 0).getTime() - new Date(b.pinTime ?? 0).getTime()
      );
      const sortedItemsWithoutPinTime = [...itemsWithoutPinTime].sort(
        (a, b) =>
          new Date(b.publishTime ?? b.scheduleTime ?? b.createTime).getTime() -
          new Date(a.publishTime ?? b.scheduleTime ?? b.createTime).getTime()
      );

      set(contentFeedSearchResult.value, "result", [...sortedItemsWithPinTime, ...sortedItemsWithoutPinTime]);
    };
    const onFeedDelete = (feedId: string) => {
      if (!contentFeedSearchResult.value) return;
      set(
        contentFeedSearchResult.value,
        "result",
        contentFeedSearchResult.value.result.filter(item => item.id !== feedId)
      );
    };

    const getContentFeed = async (
      data: Partial<InputContentFeedSearch>,
      replace: boolean = true,
      feedIdToFind?: string
    ) => {
      try {
        scroll.value = false;
        let response = await contentFeedSearch(
          {
            userId: props.userId,
            sort: contentFeedSearchSort.value,
            processingStatus: canUpdateContentSet.value ? undefined : "OK",
            published: true,
            includeScheduled: canUpdateContentSet.value,
            types: [ContentFeedTypeEnum.MEDIA, ContentFeedTypeEnum.EVENT],
            ...data,
          },
          feedIdToFind,
          feedFields()
        );
        if (replace) {
          contentFeedSearchResult.value = response;
        } else {
          contentFeedSearchResult.value = {
            ...response,
            result: [
              ...(contentFeedSearchResult.value ? contentFeedSearchResult.value.result : []),
              ...response.result,
            ],
          };
        }
        return contentFeedSearchResult.value;
      } finally {
        scroll.value = contentFeedSearchResult.value?.hasNextPage ?? false;
      }
    };
    const onNext = async (cb?: Function) => {
      try {
        if (contentFeedSearchResult.value && contentFeedSearchResult.value.hasNextPage) {
          await getContentFeed({ page: contentFeedSearchResult.value.page + 1 }, false);
        }
      } finally {
        cb && cb();
      }
    };

    const getFollowingData = async (authUserId: string, profileUserId: string) => {
      isFollowing.value = -1;
      await providers.FollowingsProvider.searchFollowings({ ids: [profileUserId] }).then(r => {
        isFollowing.value = r.find(i => i.followerId === authUserId && i.userId === profileUserId) ? 1 : 0;
      });
    };

    const checkActiveSubscriptions = async () => {
      if (authUser.value && checkRole(user.value?.role ?? "", "creator")) {
        activeSubscriptions.value = (
          await providers.SubscriptionProvider.search({
            data: {
              consumerIds: [authUser.value.id],
              providerId: props.userId,
              expired: false,
            },
          })
        ).result;
      }
    };

    const getSubscriptionLevel = async () => {
      if (user.value?.verified) {
        subscriptionLevels.value = await providers.SubscriptionLevelProvider.find(
          { userIds: [user.value.id] },
          SUBSCRIPTION_LEVEL_FRAGMENT
        );
      }
    };

    const getUser = async (userId: string = props.userId) => {
      const response = await providers.UserPublicDataProvider.search(
        { data: { id: [userId] } },
        userPublicDataFieldsWithStoryInfo()
      );
      user.value = response.result[0];
    };

    const getCallOptions = async () => {
      if (!config.featureCalling || !isUserCreator.value || !authUser.value) return;

      userCallOptions.value =
        (await providers.CallOptionsProvider.find({ userIds: [props.userId] })).find(i => i.userId === props.userId) ??
        null;
    };

    const getProfileData = async (scrollToTop = false) => {
      try {
        loading.value = true;
        user.value = null;
        questionaryInfo.value = {};
        contentFeedSearchResult.value = null;
        activeSubscriptions.value = [];
        subscriptionLevels.value = [];
        const importantPromises: Promise<any>[] = [
          getUser(props.userId),
          providers.QuestionaryInfoProvider.find({ id: props.userId }).then(r => (questionaryInfo.value = r.fields)),
          getContentFeed({}, true, props.feedId || undefined),
          providers.PaymentPackageProvider.findAll({ type: [PaymentPackageEnum.FILESET] }).then(
            r => (paymentPackages.value = r)
          ),
          getBanners(),
          checkAppointments(props.userId),
          getProfileLinks(),
        ];
        if (authUser.value && !isAuthor.value) {
          importantPromises.push(getFollowingData(authUser.value.id, props.userId));
        }
        await Promise.all(importantPromises).then(() => (loading.value = false));
        const promises = [getSubscriptionLevel(), checkActiveSubscriptions(), getCallOptions()];
        if (isUserCreator.value) {
          promises.push(checkServices());
        }
        await Promise.all(promises);
      } finally {
        loading.value = false;
        if (scrollToTop) {
          window.scrollTo({ top: 0 });
        }
      }
    };

    const updateFeed = async (payload: { userId: string; feedId?: string }) => {
      if (!contentFeedSearchResult.value) {
        return getContentFeed({}, true);
      }
      set(
        contentFeedSearchResult.value,
        "result",
        await getPurchasedContentFeeds(
          { authorId: payload.userId, purchasedFeedId: payload.feedId },
          contentFeedSearchResult.value,
          {
            includeScheduled: canUpdateContentSet.value,
            types: [ContentFeedTypeEnum.MEDIA, ContentFeedTypeEnum.EVENT],
          },
          feedFields()
        )
      );
    };
    const updateDataForLogin = (newAuthUser: User | null, oldAuthUser: User | null) => {
      if (newAuthUser && !isAuthor.value) {
        getFollowingData(newAuthUser.id, props.userId);
      }
      if (!oldAuthUser && newAuthUser && newAuthUser.accountInfo.lastLoginTime && allowAccess.value) {
        getCallOptions();
        const resultData = contentFeedSearchResult.value?.result ?? [];
        const userId = resultData.find(() => true)?.user.id;
        if (userId) {
          updateFeed({ userId });
        }
      }
    };

    const showStories = () => {
      if (!config.featureStories) return;
      if (props.storyId && user.value)
        if (checkRole(user?.value.role ?? "", "creator")) {
          emitter.emit<ViewStoriesSettings>(EVENTS.STORIES_SHOW, {
            storyPublishers: [{ userId: props.userId, user: user.value }],
            currentStoryId: props.storyId,
            back: getProfileUrl(user.value),
            routeSupport: true,
          });
        } else {
          if (user.value) root.$router.replace(getProfileUrl(user.value));
        }
    };

    const onProfileLinksUpdate = async (cb: (links: ProfileLink[]) => void) => {
      await getProfileLinks();
      cb(profileLinks.value);
    };

    watch(toRef(props, "userId"), async (newUserId, oldUserId) => {
      await getProfileData(newUserId !== oldUserId);
      isSensitiveContent.value && checkNeedShowConfirm();
    });

    watch(authUser, (newAuthUser, oldAuthUser) => {
      updateDataForLogin(newAuthUser, oldAuthUser);
    });

    watch(showNotSafeForWork, (newV, oldV) => {
      if (newV !== oldV) getContentFeed({}, true, props.feedId || undefined);
    });

    getProfileData().then(() => {
      isSensitiveContent.value && checkNeedShowConfirm();
      showStories();
    });

    onMounted(() => {
      emitter.on<any>(EVENTS.CONTENT_FEED_UPDATED, updateFeed);
      emitter.on<any>(EVENTS.CONTENT_FEED_PURCHASED, updateFeed);
      emitter.on<any>(EVENTS.CONTENT_FEED_PURCHASED, checkActiveSubscriptions);
    });

    onUnmounted(() => {
      emitter.off<any>(EVENTS.CONTENT_FEED_UPDATED, updateFeed);
      emitter.off<any>(EVENTS.CONTENT_FEED_PURCHASED, updateFeed);
      emitter.off<any>(EVENTS.CONTENT_FEED_PURCHASED, checkActiveSubscriptions);
    });

    return {
      user,
      scroll,
      loading,
      isAuthor,
      isFollowing,
      paymentPackages,
      questionaryInfo,
      subscriptionLevels,
      isBookingAvailable,
      canUpdateContentSet,
      activeSubscriptions,
      contentFeedSearchResult,
      isCallable,
      banners,
      profileLinks,
      hasServices,
      loadingCheckServices,
      onNext,
      onFeedClick,
      onFeedDelete,
      onFollowUpdate,
      onContentFeedUpdate,
      getUser,
      onProfileLinksUpdate,
      checkServices,
    };
  },
  beforeRouteEnter(to: Route, from: Route, next: any) {
    resolveUserIdByRoute(to, from, next);
  },
  beforeRouteUpdate(to: Route, from: Route, next: any) {
    resolveUserIdByRoute(to, from, next);
  },
});
